import { FC, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Alert, Button, Card, Descriptions, Input, Modal, Space, Table, Tag, Typography } from "antd"
import { ValidationCheck, ValidationSeverity } from "../store/types"

const SEVERITY_COLOR: Record<ValidationSeverity, string> = {
  PASS: "green",
  WARN: "gold",
  FAIL: "red",
}

// FAIL first, then WARN, then PASS: the operator only cares about what is wrong.
const SEVERITY_ORDER: Record<ValidationSeverity, number> = { FAIL: 0, WARN: 1, PASS: 2 }

export const BatchValidation: FC<{ batchId: string }> = observer(({ batchId }) => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const [running, setRunning] = useState(false)
  const [rebuilding, setRebuilding] = useState(false)
  const [rebuildOpen, setRebuildOpen] = useState(false)
  const [reason, setReason] = useState("")

  // Only a batch that already published artifacts has anything to republish.
  // Before that, the remedy is still cancel + re-execute.
  const status = store.batchDetail?.batch?.status
  const canRebuild = status === "READY" || status === "DELIVERED" || status === "DISPUTED"

  const onRebuild = async() => {
    setRebuilding(true)
    const ok = await store.rebuildManifest(batchId, reason.trim())
    setRebuilding(false)
    if (ok) {
      setRebuildOpen(false)
      setReason("")
    }
  }

  const result = store.validation?.batchId === batchId ? store.validation : null

  const onRun = async() => {
    setRunning(true)
    await store.validateBatch(batchId)
    setRunning(false)
  }

  const checks = [...(result?.checks ?? [])].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || a.key.localeCompare(b.key),
  )
  const failures = checks.filter(c => c.severity === "FAIL").length
  const warnings = checks.filter(c => c.severity === "WARN").length

  return (
    <Card
      size="small"
      title="Delivery validation"
      extra={<Button size="small" loading={running} onClick={onRun}>{result ? "Re-run" : "Validate"}</Button>}
    >
      {!result && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          Reads back the manifest, sessions and receipt the batch actually wrote and checks them against the contract. Report
          only — nothing is modified.
        </Typography.Paragraph>
      )}

      {result && (
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          <Descriptions column={4} size="small">
            <Descriptions.Item label="Result">
              {result.passed ? <Tag color="green">PASSED</Tag> : <Tag color="red">{failures} FAILED</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Warnings">{warnings}</Descriptions.Item>
            <Descriptions.Item label="manifest.json">{result.manifestPresent ? "✓" : "—"}</Descriptions.Item>
            <Descriptions.Item label="sessions.json">{result.sessionsPresent ? "✓" : "—"}</Descriptions.Item>
          </Descriptions>

          {!result.passed && (
            <Alert
              type="error"
              showIcon
              message="This delivery contradicts itself or the contract"
              description={
                canRebuild
                  ? "Validation is report only. If the objects are correct and only the JSON describing them is wrong — a stale "
                    + "anchor, say — republish the manifest below. If the delivered CONTENT is wrong, cancel and re-execute "
                    + "instead: republishing would hide that defect while leaving the objects untouched."
                  : "Validation is report only. The fix is to cancel the batch and re-execute with a corrected selection — "
                    + "patching the delivered manifest would hide the defect while leaving the objects it describes untouched."
              }
              action={canRebuild
                ? <Button size="small" danger onClick={() => setRebuildOpen(true)}>Republish manifest</Button>
                : undefined}
            />
          )}
          {result.passed && warnings > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${warnings} warning(s) — delivery is coherent but degraded`}
              description="Warnings do not block delivery. They are worth chasing before the next batch."
            />
          )}

          <Table
            rowKey="key"
            size="small"
            dataSource={checks}
            pagination={false}
            columns={[
              {
                title: "",
                dataIndex: "severity",
                key: "severity",
                width: 80,
                render: (s: ValidationSeverity) => <Tag color={SEVERITY_COLOR[s]}>{s}</Tag>,
              },
              { title: "Check", dataIndex: "label", key: "label" },
              { title: "Count", dataIndex: "count", key: "count", width: 80 },
              {
                title: "Detail",
                key: "detail",
                render: (_: unknown, c: ValidationCheck) => (
                  <Space direction="vertical" size={0}>
                    {c.detail && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{c.detail}</Typography.Text>}
                    {!!c.samples.length && (
                      <Typography.Text style={{ fontFamily: "monospace", fontSize: 11 }}>
                        {c.samples.join(", ")}
                        {c.count > c.samples.length ? ` … +${c.count - c.samples.length} more` : ""}
                      </Typography.Text>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </Space>
      )}
      <Modal
        open={rebuildOpen}
        title="Republish the delivered manifest"
        okText="Republish"
        okButtonProps={{ danger: true, disabled: !reason.trim(), loading: rebuilding }}
        onOk={onRebuild}
        onCancel={() => setRebuildOpen(false)}
      >
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            Regenerates <Typography.Text code>manifest.json</Typography.Text>,{" "}
            <Typography.Text code>receipt.json</Typography.Text> and{" "}
            <Typography.Text code>sessions.json</Typography.Text> over the prefix this batch already published. The delivered
            objects, its items, its quota and its status are left exactly as they are.
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            The customer may already hold the old copy, so the reason is stamped into the new files and recorded on the audit
            trail. Validation re-runs automatically afterwards.
          </Typography.Paragraph>
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Why is this being republished? e.g. anchors corrected by PR#99; playtime UTCs were ~7.4s early"
          />
        </Space>
      </Modal>
    </Card>
  )
})
